import type { BuildingType, PlayerRace } from '@kaeliux/shared';

// Humans
import hMetalMine from '../assets/images/humans/buildings/metalMine.png';
import hMineralRefinery from '../assets/images/humans/buildings/mineralRefinery.png';
import hGasPlant from '../assets/images/humans/buildings/gasPlant.png';
import hSolarPlant from '../assets/images/humans/buildings/solarPlant.png';
import hFusionPlant from '../assets/images/humans/buildings/fusionPlant.png';
import hHangars1 from '../assets/images/humans/buildings/hangars-1.png';
import hHangars2 from '../assets/images/humans/buildings/hangars-2.png';
import hMetalStorage from '../assets/images/humans/buildings/metalStorage.png';
import hMineralStorage from '../assets/images/humans/buildings/mineralStorage.png';
import hGasStorage from '../assets/images/humans/buildings/gasStorage.png';
import hRobotFactory from '../assets/images/humans/buildings/robotFactory.png';
import hAutomationCenter from '../assets/images/humans/buildings/automationCenter.png';
import hRecyclingPlant1 from '../assets/images/humans/buildings/recyclingPlant-1.png';
import hRecyclingPlant2 from '../assets/images/humans/buildings/recyclingPlant-2.png';
import hTradeCenter from '../assets/images/humans/buildings/tradeCenter.png';
import hShipyard1 from '../assets/images/humans/buildings/shipyard-1.png';
import hShipyard2 from '../assets/images/humans/buildings/shipyard-2.png';
import hLaboratory1 from '../assets/images/humans/buildings/laboratory-1.png';
import hLaboratory2 from '../assets/images/humans/buildings/laboratory-2.png';
import hLaboratory3 from '../assets/images/humans/buildings/laboratory-3.png';
import hTechCenter from '../assets/images/humans/buildings/techCenter.png';
import hGovernment from '../assets/images/humans/buildings/government.png';

// Robots
import rMetalMine from '../assets/images/robots/buildings/metalMine.png';
import rMineralRefinery from '../assets/images/robots/buildings/mineralRefinery.png';
import rGasPlant from '../assets/images/robots/buildings/gasPlant.png';
import rSolarPlant from '../assets/images/robots/buildings/solarPlant.png';
import rFusionPlant from '../assets/images/robots/buildings/fusionPlant.png';
import rHangars1 from '../assets/images/robots/buildings/hangars-1.png';
import rHangars2 from '../assets/images/robots/buildings/hangars-2.png';
import rMetalStorage from '../assets/images/robots/buildings/metalStorage.png';
import rMineralStorage from '../assets/images/robots/buildings/mineralStorage.png';
import rGasStorage from '../assets/images/robots/buildings/gasStorage.png';
import rRobotFactory from '../assets/images/robots/buildings/robotFactory.png';
import rAutomationCenter from '../assets/images/robots/buildings/automationCenter.png';
import rRecyclingPlant1 from '../assets/images/robots/buildings/recyclingPlant-1.png';
import rRecyclingPlant2 from '../assets/images/robots/buildings/recyclingPlant-2.png';
import rTradeCenter from '../assets/images/robots/buildings/tradeCenter.png';
import rShipyard1 from '../assets/images/robots/buildings/shipyard-1.png';
import rShipyard2 from '../assets/images/robots/buildings/shipyard-2.png';
import rLaboratory1 from '../assets/images/robots/buildings/laboratory-1.png';
import rLaboratory2 from '../assets/images/robots/buildings/laboratory-2.png';
import rLaboratory3 from '../assets/images/robots/buildings/laboratory-3.png';
import rTechCenter from '../assets/images/robots/buildings/techCenter.png';
import rGovernment from '../assets/images/robots/buildings/government.png';

// Axons
import aMetalMine from '../assets/images/axons/buildings/metalMine.png';
import aMineralRefinery from '../assets/images/axons/buildings/mineralRefinery.png';
import aGasPlant from '../assets/images/axons/buildings/gasPlant.png';
import aSolarPlant from '../assets/images/axons/buildings/solarPlant.png';
import aFusionPlant from '../assets/images/axons/buildings/fusionPlant.png';
import aHangars1 from '../assets/images/axons/buildings/hangars-1.png';
import aHangars2 from '../assets/images/axons/buildings/hangars-2.png';
import aMetalStorage from '../assets/images/axons/buildings/metalStorage.png';
import aMineralStorage from '../assets/images/axons/buildings/mineralStorage.png';
import aGasStorage from '../assets/images/axons/buildings/gasStorage.png';
import aRobotFactory from '../assets/images/axons/buildings/robotFactory.png';
import aAutomationCenter from '../assets/images/axons/buildings/automationCenter.png';
import aRecyclingPlant1 from '../assets/images/axons/buildings/recyclingPlant-1.png';
import aRecyclingPlant2 from '../assets/images/axons/buildings/recyclingPlant-2.png';
import aTradeCenter from '../assets/images/axons/buildings/tradeCenter.png';
import aShipyard1 from '../assets/images/axons/buildings/shipyard-1.png';
import aShipyard2 from '../assets/images/axons/buildings/shipyard-2.png';
import aLaboratory1 from '../assets/images/axons/buildings/laboratory-1.png';
import aLaboratory2 from '../assets/images/axons/buildings/laboratory-2.png';
import aLaboratory3 from '../assets/images/axons/buildings/laboratory-3.png';
import aTechCenter from '../assets/images/axons/buildings/techCenter.png';
import aGovernment from '../assets/images/axons/buildings/government.png';

const DEFAULT_RACE: PlayerRace = 'humans';

/**
 * One visual tier of a building's sprite. `maxLevel` is the highest level
 * this tier is shown for; `null` means "everything above the previous
 * tier's maxLevel" (i.e. the final tier).
 */
interface BuildingSpriteTier {
    maxLevel: number | null;
    images: Record<PlayerRace, string>;
}

/**
 * Sprite tiers per building, sliced from `docs/images/{race}_building_sprites.png`
 * (4x8 grids) per the row/col mapping the buildings art was authored to.
 * Buildings with a single tier show the same image at every level, including
 * level 0 (not yet built) — used as a "preview" icon in the zone list.
 */
const BUILDING_SPRITES: Record<BuildingType, [BuildingSpriteTier, ...BuildingSpriteTier[]]> = {
    metalMine: [{ maxLevel: null, images: { humans: hMetalMine, robots: rMetalMine, axons: aMetalMine } }],
    mineralRefinery: [{ maxLevel: null, images: { humans: hMineralRefinery, robots: rMineralRefinery, axons: aMineralRefinery } }],
    gasPlant: [{ maxLevel: null, images: { humans: hGasPlant, robots: rGasPlant, axons: aGasPlant } }],
    solarPlant: [{ maxLevel: null, images: { humans: hSolarPlant, robots: rSolarPlant, axons: aSolarPlant } }],
    fusionPlant: [{ maxLevel: null, images: { humans: hFusionPlant, robots: rFusionPlant, axons: aFusionPlant } }],
    hangars: [
        { maxLevel: 10, images: { humans: hHangars1, robots: rHangars1, axons: aHangars1 } },
        { maxLevel: null, images: { humans: hHangars2, robots: rHangars2, axons: aHangars2 } },
    ],
    metalStorage: [{ maxLevel: null, images: { humans: hMetalStorage, robots: rMetalStorage, axons: aMetalStorage } }],
    mineralStorage: [{ maxLevel: null, images: { humans: hMineralStorage, robots: rMineralStorage, axons: aMineralStorage } }],
    gasStorage: [{ maxLevel: null, images: { humans: hGasStorage, robots: rGasStorage, axons: aGasStorage } }],
    robotFactory: [{ maxLevel: null, images: { humans: hRobotFactory, robots: rRobotFactory, axons: aRobotFactory } }],
    automationCenter: [{ maxLevel: null, images: { humans: hAutomationCenter, robots: rAutomationCenter, axons: aAutomationCenter } }],
    recyclingPlant: [
        { maxLevel: 4, images: { humans: hRecyclingPlant1, robots: rRecyclingPlant1, axons: aRecyclingPlant1 } },
        { maxLevel: null, images: { humans: hRecyclingPlant2, robots: rRecyclingPlant2, axons: aRecyclingPlant2 } },
    ],
    tradeCenter: [{ maxLevel: null, images: { humans: hTradeCenter, robots: rTradeCenter, axons: aTradeCenter } }],
    shipyard: [
        { maxLevel: 9, images: { humans: hShipyard1, robots: rShipyard1, axons: aShipyard1 } },
        { maxLevel: null, images: { humans: hShipyard2, robots: rShipyard2, axons: aShipyard2 } },
    ],
    laboratory: [
        { maxLevel: 9, images: { humans: hLaboratory1, robots: rLaboratory1, axons: aLaboratory1 } },
        { maxLevel: 14, images: { humans: hLaboratory2, robots: rLaboratory2, axons: aLaboratory2 } },
        { maxLevel: null, images: { humans: hLaboratory3, robots: rLaboratory3, axons: aLaboratory3 } },
    ],
    techCenter: [{ maxLevel: null, images: { humans: hTechCenter, robots: rTechCenter, axons: aTechCenter } }],
    government: [{ maxLevel: null, images: { humans: hGovernment, robots: rGovernment, axons: aGovernment } }],
};

/**
 * Resolves the sprite for a building at a given level, for the player's race.
 * Falls back to {@link DEFAULT_RACE} when the player hasn't picked a race yet.
 */
export function getBuildingIcon(buildingType: BuildingType, level: number, race: PlayerRace | undefined): string {
    const tiers = BUILDING_SPRITES[buildingType];
    const tier = tiers.find((t) => t.maxLevel === null || level <= t.maxLevel) ?? tiers[0];
    return tier.images[race ?? DEFAULT_RACE];
}
